import type { Currency } from '@/lib/currency';
import type {
  ListingFormFieldError,
  ListingFormMode,
  ListingFormNormalizedPayload,
  ListingFormStep,
  ListingFormValidationResult,
  ListingFormValues,
} from './types';
import { getCountryOption, isValidRegionForCountry, normalizeRegionValue } from './countries';
import { isKnownPropertyType, normalizePropertyType } from './propertyTypes';
import { getCoverIndex } from './imageModel';
import { isBlobUrl } from '@/lib/listingImages';
import { normalizeDealType, requiresFinancingTerms } from '@/lib/dealTypes';
import { createEmptyFinancingFields, validateFinancingForDealType } from '@/lib/financingTerms';

function parseOptionalNumber(
  raw: string,
  field: ListingFormFieldError['field'],
  step: ListingFormStep,
  opts: { allowDecimal?: boolean; min?: number; max?: number; label: string }
): { value: number | null; error: ListingFormFieldError | null } {
  const trimmed = raw.trim();
  if (!trimmed) return { value: null, error: null };

  const cleaned = trimmed.replace(/,/g, '');
  const n = Number(cleaned);
  if (!Number.isFinite(n) || Number.isNaN(n)) {
    return {
      value: null,
      error: { field, step, message: `${opts.label} must be a valid number.` },
    };
  }
  if (!opts.allowDecimal && !Number.isInteger(n)) {
    return {
      value: null,
      error: { field, step, message: `${opts.label} must be a whole number.` },
    };
  }
  if (opts.min != null && n < opts.min) {
    return {
      value: null,
      error: { field, step, message: `${opts.label} cannot be less than ${opts.min}.` },
    };
  }
  if (opts.max != null && n > opts.max) {
    return {
      value: null,
      error: { field, step, message: `${opts.label} cannot be greater than ${opts.max}.` },
    };
  }
  return { value: n, error: null };
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export function validateListingForm(
  values: ListingFormValues,
  mode: ListingFormMode,
  options?: { steps?: ListingFormStep[] }
): ListingFormValidationResult {
  const steps = options?.steps ?? ([1, 2, 3, 4] as ListingFormStep[]);
  const include = (step: ListingFormStep) => steps.includes(step);
  const errors: ListingFormFieldError[] = [];

  const country = values.country.trim() || 'US';
  const countryMeta = getCountryOption(country);
  const address = values.address.trim();
  const city = values.city.trim();
  const state = normalizeRegionValue(country, values.state);
  const zip = values.zip.trim();
  const dealType = normalizeDealType(values.deal_type);
  const financing = values.financing ?? createEmptyFinancingFields();

  if (include(1)) {
    if (!country) errors.push({ field: 'country', step: 1, message: 'Country is required.' });
    if (!address) errors.push({ field: 'address', step: 1, message: 'Street address is required.' });
    if (!city) errors.push({ field: 'city', step: 1, message: 'City is required.' });
    if (!state) {
      errors.push({ field: 'state', step: 1, message: `${countryMeta.regionLabel} is required.` });
    } else if (!isValidRegionForCountry(country, state)) {
      errors.push({
        field: 'state',
        step: 1,
        message: `Select a valid ${countryMeta.regionLabel.toLowerCase()}.`,
      });
    }
    if (!zip) {
      errors.push({ field: 'zip', step: 1, message: `${countryMeta.postalLabel} is required.` });
    }

    const lat = values.latitude;
    const lng = values.longitude;
    if (
      lat == null ||
      lng == null ||
      !Number.isFinite(lat) ||
      !Number.isFinite(lng) ||
      values.pinStatus === 'not_geocoded' ||
      values.pinStatus === 'error' ||
      values.pinStatus === 'geocoding' ||
      values.pinStatus === 'needs_reconfirm'
    ) {
      errors.push({
        field: 'pin',
        step: 1,
        message:
          values.pinStatus === 'needs_reconfirm'
            ? 'Address changed. Re-geocode or confirm the map pin before continuing.'
            : 'Confirm the map pin location before continuing.',
      });
    }
  }

  const propertyType = normalizePropertyType(values.property_type);
  let price = 0;
  let arv: number | null = null;
  let repairs: number | null = null;
  let beds: number | null = null;
  let baths: number | null = null;
  let sqft: number | null = null;
  let lotSqft: number | null = null;
  let garage: number | null = null;
  let yearBuilt: number | null = null;
  const currency: Currency =
    values.currency === 'USD' ||
    values.currency === 'CAD' ||
    values.currency === 'GBP' ||
    values.currency === 'AUD'
      ? values.currency
      : 'USD';

  if (include(2)) {
    if (!dealType) {
      errors.push({
        field: 'deal_type',
        step: 2,
        message: 'Select how this deal is being offered.',
      });
    }

    if (!propertyType || !isKnownPropertyType(propertyType)) {
      errors.push({ field: 'property_type', step: 2, message: 'Property type is required.' });
    }

    const priceParsed = parseOptionalNumber(values.price, 'price', 2, {
      allowDecimal: true,
      min: 0,
      label: 'Asking price',
    });
    if (priceParsed.error) errors.push(priceParsed.error);
    else if (priceParsed.value == null || priceParsed.value <= 0) {
      errors.push({ field: 'price', step: 2, message: 'Asking price must be greater than zero.' });
    } else price = priceParsed.value;

    const arvParsed = parseOptionalNumber(values.arv, 'arv', 2, {
      allowDecimal: true,
      min: 0,
      label: 'ARV',
    });
    if (arvParsed.error) errors.push(arvParsed.error);
    else arv = arvParsed.value;

    const repairsParsed = parseOptionalNumber(values.repairs, 'repairs', 2, {
      allowDecimal: true,
      min: 0,
      label: 'Estimated repairs',
    });
    if (repairsParsed.error) errors.push(repairsParsed.error);
    else repairs = repairsParsed.value;

    const bedsParsed = parseOptionalNumber(values.beds, 'beds', 2, {
      allowDecimal: false,
      min: 0,
      label: 'Beds',
    });
    if (bedsParsed.error) errors.push(bedsParsed.error);
    else beds = bedsParsed.value;

    const bathsParsed = parseOptionalNumber(values.baths, 'baths', 2, {
      allowDecimal: true,
      min: 0,
      label: 'Baths',
    });
    if (bathsParsed.error) errors.push(bathsParsed.error);
    else baths = bathsParsed.value;

    const sqftParsed = parseOptionalNumber(values.sqft, 'sqft', 2, {
      allowDecimal: false,
      min: 0,
      label: 'Living area',
    });
    if (sqftParsed.error) errors.push(sqftParsed.error);
    else sqft = sqftParsed.value;

    const lotParsed = parseOptionalNumber(values.lot_sqft, 'lot_sqft', 2, {
      allowDecimal: false,
      min: 0,
      label: 'Lot size',
    });
    if (lotParsed.error) errors.push(lotParsed.error);
    else lotSqft = lotParsed.value;

    const garageParsed = parseOptionalNumber(values.garage_spaces, 'garage_spaces', 2, {
      allowDecimal: false,
      min: 0,
      max: 20,
      label: 'Garage spaces',
    });
    if (garageParsed.error) errors.push(garageParsed.error);
    else garage = garageParsed.value;

    const currentYear = new Date().getFullYear();
    const yearParsed = parseOptionalNumber(values.year_built, 'year_built', 2, {
      allowDecimal: false,
      min: 1800,
      max: currentYear + 1,
      label: 'Year built',
    });
    if (yearParsed.error) errors.push(yearParsed.error);
    else yearBuilt = yearParsed.value;

    if (dealType && requiresFinancingTerms(dealType)) {
      for (const fe of validateFinancingForDealType(dealType, financing)) {
        errors.push({
          field: fe.field as ListingFormFieldError['field'],
          step: 2,
          message: fe.message,
        });
      }
    }
  }

  if (include(3)) {
    const email = values.contact_email.trim();
    if (email && !isValidEmail(email)) {
      errors.push({ field: 'contact_email', step: 3, message: 'Enter a valid contact email.' });
    }
    for (const img of values.images) {
      if (img.storageKey && isBlobUrl(img.storageKey)) {
        errors.push({
          field: 'images',
          step: 3,
          message: 'Invalid image reference detected. Remove and re-add the photo.',
        });
        break;
      }
    }
  }

  if (include(4) && mode === 'create' && !values.seller_acknowledged) {
    errors.push({
      field: 'seller_acknowledged',
      step: 4,
      message: 'Confirm you are authorized to market this deal before publishing.',
    });
  }

  const coverIndex = getCoverIndex(values.images);
  const existingImageKeys = values.images
    .filter((img) => img.storageKey && !isBlobUrl(img.storageKey))
    .map((img) => img.storageKey as string);
  const newImageFiles = values.images
    .filter((img) => img.file != null)
    .map((img) => img.file as File);

  const lat = values.latitude;
  const lng = values.longitude;
  const clearFinancing =
    Boolean(dealType) && !requiresFinancingTerms(dealType);

  const normalized: ListingFormNormalizedPayload = {
    country,
    address,
    city,
    state,
    zip,
    price,
    currency,
    arv,
    repairs,
    beds,
    baths,
    sqft,
    lot_sqft: lotSqft,
    garage_spaces: garage,
    year_built: yearBuilt,
    property_type: propertyType || 'single-family',
    deal_type: dealType,
    age_restricted: Boolean(values.age_restricted),
    description: values.description.trim() || null,
    contact_name: values.contact_name.trim() || null,
    contact_phone: values.contact_phone.trim() || null,
    contact_email: values.contact_email.trim() || null,
    deal_constraints: [...values.deal_constraints],
    deal_constraints_notes: values.deal_constraints_notes.trim() || null,
    seller_acknowledged: values.seller_acknowledged,
    latitude: lat != null && Number.isFinite(lat) ? lat : 0,
    longitude: lng != null && Number.isFinite(lng) ? lng : 0,
    existingImageKeys,
    coverStorageKey:
      coverIndex != null && values.images[coverIndex]?.storageKey
        ? values.images[coverIndex].storageKey
        : null,
    newImageFiles,
    coverIndex,
    financing,
    clear_financing_terms: clearFinancing,
  };

  return { ok: errors.length === 0, errors, summary: errors, normalized };
}

export function validateListingFormStep(
  values: ListingFormValues,
  mode: ListingFormMode,
  step: ListingFormStep
): ListingFormValidationResult {
  return validateListingForm(values, mode, { steps: [step] });
}
